import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { LifeBuoy } from "lucide-react";

const FAQS = [
  {
    id: "what-is-a-hook",
    q: "What is a hook?",
    a: [
      "A hook is the very first line of your video, post or email — the words that decide whether someone keeps watching or scrolls past.",
      "Think of it like the opening a friend uses when they've got something exciting to tell you: \"You won't believe what just happened…\" It grabs attention and makes people want the rest.",
      "In Hook2Income, we write hooks for you so your content stops the scroll from the first second.",
    ],
  },
  {
    id: "what-is-an-affiliate-product",
    q: "What is an affiliate product?",
    a: [
      "An affiliate product is something made by someone else that you recommend. When a person buys it through your special link, you earn a small commission.",
      "You don't create the product, hold any stock, or handle the delivery. Your job is simply to share it with people who'd find it useful.",
      "It's a popular way to earn online because you can start today with a product you already like.",
    ],
  },
  {
    id: "how-credits-work",
    q: "How do credits work?",
    a: [
      "Credits are what you spend when the app generates something for you with AI — like a set of hooks, a full course, or a social content pack.",
      "Different actions cost different amounts. Smaller things (like idea suggestions) cost a little; bigger things (like a whole content pack) cost more. You'll always see the cost before you generate.",
      "Your first Guided journey is free. After that, you can top up credits any time from the Buy Credits page, and you only pay for what you use.",
    ],
  },
  {
    id: "what-to-do-with-content",
    q: "What do I do with my content once it's generated?",
    a: [
      "Everything the app creates is yours to use straight away. You can copy any hook, script, email or post with one tap.",
      "Post the videos and captions to your social accounts, send the email to your list, and use the lead magnet to grow your audience. Most pieces are ready to publish as-is.",
      "You can also design matching graphics in Canva using the buttons on each result, or push a whole funnel to systeme.io in one click.",
    ],
  },
  {
    id: "guided-vs-advanced",
    q: "What's the difference between Guided Mode and Advanced Mode?",
    a: [
      "Guided Mode is a friendly, step-by-step journey. It asks a few simple questions, then walks you through building your plan one piece at a time. It's the easiest place to start if you're new.",
      "Advanced Mode gives you the full toolkit on your own terms — jump straight into Income Mode, Smart Hooks, campaigns and projects whenever you like.",
      "You can switch between the two at any time using the Mode toggle in the sidebar. Nothing you've made gets lost.",
    ],
  },
  {
    id: "export-to-systeme",
    q: "How do I export my content to systeme.io?",
    a: [
      "systeme.io is a free tool for building sales funnels and email lists. Hook2Income can send your funnel there for you.",
      "First, add your systeme.io API key in Settings (you create the key inside your systeme.io account). Then open a campaign, find the systeme.io export section, and click \"Send to systeme.io\".",
      "The app builds your opt-in, sales and thank-you pages, fills in the copy, saves your promo email, and tags new leads automatically. If a page can't be filled, you can copy each block and paste it in yourself.",
    ],
  },
];

export default function Help() {
  return (
    <div className="mx-auto max-w-3xl" data-testid="help-page">
      <div className="mb-8 fade-up">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <LifeBuoy size={24} />
        </div>
        <h1 className="font-head text-4xl font-bold">Help &amp; FAQ</h1>
        <p className="mt-2 text-muted-foreground">
          New here? These quick answers cover the basics in plain English. Tap any question to expand it.
        </p>
      </div>

      <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card px-5 sm:px-6"
        data-testid="help-accordion">
        {FAQS.map((f) => (
          <AccordionItem key={f.id} value={f.id} data-testid={`faq-item-${f.id}`}>
            <AccordionTrigger data-testid={`faq-trigger-${f.id}`}
              className="text-base font-semibold">
              {f.q}
            </AccordionTrigger>
            <AccordionContent data-testid={`faq-content-${f.id}`}>
              <div className="space-y-3 pr-1 text-sm leading-relaxed text-muted-foreground">
                {f.a.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <p className="mt-6 text-center text-sm text-muted-foreground" data-testid="help-footer">
        Still stuck? Reach out and we'll happily point you in the right direction.
      </p>
    </div>
  );
}
